<template>
	<div
		ref="textContainer"
		class="text-container"
		:class="[
			containerClassList,
			`text-container--${textAlign}`,
			{ 'animated-container': animatedOverflow && isOverflowing },
			{
				'text-container--scroll-reset':
					overflowAnimation === 'scroll-reset',
			},
		]"
	>
		<Tooltip :placement="Placements.TOP" :disabled="!isOverflowing">
			<template #element>
				<div
					v-if="isOverflowing && animatedOverflow"
					class="animated-container__inner"
				>
					<span
						:key="text"
						ref="shrinkText"
						class="base shrink-text"
						:style="cssVars"
						:class="[
							`text-${forcedFontSize ? forcedFontSize : localFontSize}`,
							textClassList,
							{
								'shrink-text--bold': isBold,
								'animated-text':
									animatedOverflow && isOverflowing,
							},
						]"
						:title="truncatedText"
						v-html="truncatedText"
					/>
				</div>
				<span
					v-else
					ref="shrinkText"
					class="base shrink-text"
					:class="[
						`text-${forcedFontSize ? forcedFontSize : localFontSize}`,
						`text-align-${textAlign}`,
						textClassList,
						{
							'overflow-hidden':
								isOverflowing && !animatedOverflow,
							'shrink-text--bold': isBold,
							'shrink-text--uppercase': isUppercase,
							[`multi-line line-clamp-${maxLineLimit}`]:
								maxLineLimit && isOverflowing,
						},
					]"
					:style="{
						lineHeight:
							hasSameLineHeight && maxLineLimit && isOverflowing
								? forcedFontSize
									? `${forcedFontSize}px`
									: `${localFontSize}px`
								: '1.1', // vacsie ako 1 kvoli ciarkam a podobne, aby to nebolo orezane
					}"
					:title="isOverflowing ? truncatedText : ''"
					v-html="truncatedText"
				/>
			</template>
			<template #tooltip>
				<span>{{ text }}</span>
			</template>
		</Tooltip>
	</div>
</template>

<script lang="ts" setup>
import Tooltip from "@/components/Tooltip.vue";
import { FONT_SIZES, Placements, TextAlign } from "@/types/ui";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";

interface Props {
	text: string;
	fontSize: number;
	isBold?: boolean;
	isUppercase?: boolean;
	hasSameLineHeight?: boolean;
	forcedFontSize?: number | null;
	containerClassList?: string | null;
	textClassList?: string | null;
	textAlign?: TextAlign | null;
	animatedOverflow?: boolean;
	overflowAnimation?: "side-to-side" | "scroll-reset";
	maxLineLimit?: number | null;
	minFontSize?: number | null;
}

const {
	text,
	fontSize,
	isBold = false,
	isUppercase = false,
	hasSameLineHeight = false,
	forcedFontSize = null,
	containerClassList = null,
	textClassList = null,
	textAlign = TextAlign.CENTER,
	animatedOverflow = false,
	overflowAnimation = "side-to-side",
	maxLineLimit = null,
	minFontSize = null,
} = defineProps<Props>();

const fontSizePoints = ref<number[]>(FONT_SIZES);
const localFontSize = ref<number>(fontSize);
const truncatedText = ref<string>(text);
const isOverflowing = ref<boolean>(false);
const resizeCount = ref<number>(0);
const scrollOverflow = ref(0);

const textContainer = useTemplateRef<HTMLElement>("textContainer");
const textElement = useTemplateRef<HTMLElement>("shrinkText");

const emit = defineEmits(["rerender"]);

const measureScrollOverflow = (): void => {
	if (!textContainer.value || !textElement.value) return;
	const style = getComputedStyle(textContainer.value);
	const availableWidth =
		textContainer.value.clientWidth -
		parseFloat(style.paddingLeft) -
		parseFloat(style.paddingRight);
	const difference = textElement.value.scrollWidth - availableWidth;
	scrollOverflow.value = difference > 1 ? difference : 0;
	isOverflowing.value = scrollOverflow.value > 0;
};

const checkOverflow = (resize: boolean = false): void => {
	if (overflowAnimation === "scroll-reset") {
		measureScrollOverflow();
		// Remeasure after the overflowing branch mounts (v-if swap).
		void nextTick(measureScrollOverflow);
		return;
	}
	if (textContainer && textElement) {
		const containerWidth = textContainer?.value?.offsetWidth ?? 0;
		const textWidth = textElement?.value?.offsetWidth ?? 0;

		if (textWidth > containerWidth) {
			isOverflowing.value = true;
			if (resize) {
				localFontSize.value = findAndRetrieveElement(
					localFontSize.value,
				);
				resizeCount.value++;
				emit("rerender", localFontSize.value);

				// INFO: at 110 to 44 reduction by 1 jump in font size
				// 40 & lower by 2 jumps
				if (fontSize <= 40 && resizeCount.value === 1) {
					// second resize & check for isOverflowing
					checkOverflow(true);
				} else {
					// only one check for isOverflowing & :title after resizing
					checkOverflow();
				}
			}
		} else {
			isOverflowing.value = false;
		}
	}
};

const findAndRetrieveElement = (size: number): number => {
	const index = fontSizePoints.value.indexOf(size);
	if (minFontSize !== null && size <= minFontSize) {
		return minFontSize;
	}

	if (index > 0) {
		return fontSizePoints.value[index - 1];
	} else {
		return fontSizePoints.value[0];
	}
};

const cssVars = computed((): Record<string, string> => {
	return {
		"--scroll-distance": `${-scrollOverflow.value}px`,
		"--scroll-duration": `${Math.max(6, scrollOverflow.value / 18 + 3)}s`,
		"--scroll-dist": `${-Math.abs((textElement.value?.scrollWidth ?? 0) - (textContainer.value?.offsetWidth ?? 0) + 30)}px`,
	};
});

watch(
	(): string => text,
	(): void => {
		truncatedText.value = text;
		checkOverflow(true);
	},
);

watch(
	(): number => fontSize,
	(): void => {
		localFontSize.value = fontSize;
		checkOverflow(true);
	},
);

onMounted((): void => {
	// resize only once via true
	checkOverflow(true);
});
</script>

<style lang="scss" scoped>
.base {
	user-select: none;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.shrink-text {
	&--bold {
		font-weight: 800;
	}

	&--uppercase {
		text-transform: uppercase;
	}

	&.text-align {
		&-left {
			text-align: left;
		}
		&-center {
			text-align: center;
		}
		&-right {
			text-align: right;
		}
	}

	@for $i from 1 through 4 {
		&.line-clamp-#{$i} {
			display: -webkit-box;
			-webkit-line-clamp: $i;
			overflow: hidden;
			-webkit-box-orient: vertical;
		}
	}
}

.text-container {
	@include flex-row-center;
	width: 100%;

	&--left {
		justify-content: flex-start;
	}

	&--right {
		justify-content: flex-end;
	}

	:deep(.v-popper) {
		width: 100%;
	}
}

/* This class is associated with .line-clamp-* */
.multi-line {
	white-space: normal;
}

/* if the text still exceeds after resizing, apply the 'hidden' style */
.overflow-hidden {
	display: block;
	width: 100%;
	overflow: hidden;
}

.animated-container {
	position: relative;
	justify-content: left;
	padding: 0.3125rem 0.625rem;
	min-width: 0;

	&__inner {
		mask-image: linear-gradient(
			to right,
			transparent,
			black 5%,
			black 95%,
			transparent 100%
		);
		margin: -0.3125rem -0.625rem;
		padding: 0.3125rem 10% 0.3125rem 5%;
		overflow: hidden;

		&:hover {
			& .animated-text {
				animation-play-state: paused;
			}
		}
	}

	.animated-text {
		display: inline-block;
		position: relative;
		animation: side-to-side 5s ease-in-out infinite;
		// initial offset
		// padding-left: 100%;
		white-space: nowrap;
	}
	@keyframes side-to-side {
		0% {
			transform: translateX(0);
		}
		50% {
			transform: translateX(var(--scroll-dist));
		}
		100% {
			transform: translateX(0);
		}
	}
}

.text-container--scroll-reset {
	align-items: flex-start;
	box-sizing: border-box;
	padding: 0 0.25rem;

	.shrink-text {
		display: inline-block;
		width: max-content;
	}

	.animated-container__inner {
		mask-image: none;
		margin: 0;
		padding: 0;
	}

	.animated-text {
		display: block;
		animation: scroll-reset var(--scroll-duration) linear infinite;
	}

	.animated-container__inner:hover .animated-text {
		animation-play-state: running;
	}
}

@keyframes scroll-reset {
	0%,
	18% {
		transform: translateX(0);
	}
	78%,
	95% {
		transform: translateX(var(--scroll-distance));
		opacity: 1;
	}
	99% {
		transform: translateX(var(--scroll-distance));
		opacity: 0;
	}
	100% {
		transform: translateX(0);
		opacity: 0;
	}
}

@media (prefers-reduced-motion: reduce) {
	.text-container--scroll-reset .animated-text {
		display: block;
		animation: none;
		width: 100%;
		max-width: 100%;
		overflow: hidden;
		text-align: left;
		text-overflow: ellipsis;
	}
}

@keyframes leftright {
	to {
		transform: translateX(-100%);
	}
}
</style>
